/**
 * Seed Phrase Encryption Service
 *
 * Uses AES-256-GCM encryption with Argon2id key derivation
 * for securing seed phrases with a user passphrase.
 *
 * Security Features:
 * - AES-256-GCM (authenticated encryption)
 * - Argon2id KDF (resistant to GPU/ASIC attacks)
 * - Unique salt per encryption (32 bytes)
 * - Random IV per encryption (12 bytes)
 * - GCM authentication tag (16 bytes)
 * - 100% client-side, zero-knowledge
 */

import { gcm } from '@noble/ciphers/aes.js';
import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { argon2id } from '@noble/hashes/argon2.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { utf8ToBytes, bytesToUtf8 } from '@noble/ciphers/utils.js';
import { deriveKeyWithWorker, isWorkerSupported } from './cryptoWorker';

/**
 * Generate cryptographically secure random bytes
 */
function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * Encryption result structure
 */
export interface EncryptionResult {
  ciphertext: string;        // Base64 encoded
  salt: string;              // 32 bytes, Base64
  iv: string;                // 12 bytes (AES-GCM) or 24 bytes (XChaCha20), Base64
  tag: string;               // 16 bytes, Base64
  version: string;           // "v1"
  timestamp: number;
  algorithm: "AES-256-GCM" | "XChaCha20-Poly1305";
  kdf: "Argon2id";
  kdfParams: {
    memory: number;          // KB
    iterations: number;
    parallelism: number;
  };
  checksum: string;          // SHA-256 hash of ciphertext for integrity verification
  recoveryHint?: string;     // Optional hint for passphrase recovery
}

/**
 * Argon2id parameters
 * Balanced between security and performance
 */
const ARGON2_PARAMS = {
  memory: 65536,      // 64 MB
  iterations: 4,      // Increased from 3 for enhanced security
  parallelism: 4,
  keyLength: 32,      // 256 bits for AES-256 / XChaCha20
};

/**
 * Convert Uint8Array to Base64 string
 */
function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Convert Base64 string to Uint8Array
 */
function fromBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Derive encryption key from passphrase using Argon2id
 */
function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  params?: { memory: number; iterations: number; parallelism: number }
): Uint8Array {
  const passphraseBytes = utf8ToBytes(passphrase);
  const kdfParams = params || ARGON2_PARAMS;

  return argon2id(passphraseBytes, salt, {
    m: kdfParams.memory,
    t: kdfParams.iterations,
    p: kdfParams.parallelism,
    dkLen: ARGON2_PARAMS.keyLength,
  });
}

/**
 * Derive encryption key from passphrase using Argon2id (async, with optional Web Worker)
 */
async function deriveKeyAsync(
  passphrase: string,
  salt: Uint8Array,
  params?: { memory: number; iterations: number; parallelism: number },
  onProgress?: (progress: number) => void
): Promise<Uint8Array> {
  const passphraseBytes = utf8ToBytes(passphrase);
  const kdfParams = params || ARGON2_PARAMS;

  // Use Web Worker if available for better UX
  if (isWorkerSupported()) {
    try {
      return await deriveKeyWithWorker(
        passphraseBytes,
        salt,
        {
          memory: kdfParams.memory,
          iterations: kdfParams.iterations,
          parallelism: kdfParams.parallelism,
          keyLength: ARGON2_PARAMS.keyLength,
        },
        onProgress
      );
    } catch (error) {
      console.warn('Worker failed, falling back to main thread:', error);
      // Fallback to main thread
    }
  }

  // Fallback: run on main thread
  // Simulate progress for consistency
  if (onProgress) {
    const progressInterval = setInterval(() => {
      onProgress(30 + Math.random() * 40); // 30-70%
    }, 50);

    const result = deriveKey(passphrase, salt, params);
    clearInterval(progressInterval);
    onProgress(100);
    return result;
  }

  return deriveKey(passphrase, salt, params);
}

/**
 * Encrypt seed phrase with passphrase
 *
 * @param seedPhrase - The text to encrypt (1 to 40 words)
 * @param passphrase - User's encryption passphrase (min 12 chars recommended)
 * @param algorithm - Encryption algorithm to use (default: AES-256-GCM)
 * @param recoveryHint - Optional hint for passphrase recovery
 * @returns Encryption result with all necessary data for decryption
 *
 * @example
 * const result = await encryptSeedPhrase(
 *   "abandon abandon abandon ... art",
 *   "my-super-strong-passphrase-2024",
 *   "XChaCha20-Poly1305",
 *   "Prénom de ma mère + année de naissance"
 * );
 */
export async function encryptSeedPhrase(
  seedPhrase: string,
  passphrase: string,
  algorithm: "AES-256-GCM" | "XChaCha20-Poly1305" = "AES-256-GCM",
  recoveryHint?: string
): Promise<EncryptionResult> {
  if (!seedPhrase || seedPhrase.trim().length === 0) {
    throw new Error('Seed phrase cannot be empty');
  }

  if (!passphrase || passphrase.length < 8) {
    throw new Error('Passphrase must be at least 8 characters');
  }

  try {
    // Generate random salt (32 bytes)
    const salt = randomBytes(32);

    // Derive encryption key using Argon2id
    const key = deriveKey(passphrase, salt);

    // Convert seed phrase to bytes
    const plaintext = utf8ToBytes(seedPhrase.trim());

    let iv: Uint8Array;
    let ciphertext: Uint8Array;
    let tag: Uint8Array;
    let encrypted: Uint8Array;

    if (algorithm === "XChaCha20-Poly1305") {
      // Generate random IV (24 bytes for XChaCha20)
      iv = randomBytes(24);

      // Encrypt with XChaCha20-Poly1305
      const cipher = xchacha20poly1305(key, iv);
      ciphertext = cipher.encrypt(plaintext);

      // Extract authentication tag (last 16 bytes)
      tag = ciphertext.slice(-16);
      encrypted = ciphertext.slice(0, -16);
    } else {
      // AES-256-GCM (default)
      // Generate random IV (12 bytes for GCM)
      iv = randomBytes(12);

      // Encrypt with AES-256-GCM
      const cipher = gcm(key, iv);
      ciphertext = cipher.encrypt(plaintext);

      // Extract authentication tag (last 16 bytes)
      tag = ciphertext.slice(-16);
      encrypted = ciphertext.slice(0, -16);
    }

    // Calculate checksum for integrity verification
    const checksumHash = sha256(encrypted);
    const checksum = toBase64(checksumHash);

    return {
      ciphertext: toBase64(encrypted),
      salt: toBase64(salt),
      iv: toBase64(iv),
      tag: toBase64(tag),
      version: 'v1',
      timestamp: Date.now(),
      algorithm,
      kdf: 'Argon2id',
      kdfParams: {
        memory: ARGON2_PARAMS.memory,
        iterations: ARGON2_PARAMS.iterations,
        parallelism: ARGON2_PARAMS.parallelism,
      },
      checksum,
      recoveryHint,
    };
  } catch (error) {
    throw new Error(`Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Encrypt seed phrase with passphrase (with progress callback)
 *
 * This version uses Web Worker when available for non-blocking encryption.
 * Progress callback receives values from 0-100.
 *
 * @param seedPhrase - The text to encrypt (1 to 40 words)
 * @param passphrase - User's encryption passphrase (min 12 chars recommended)
 * @param algorithm - Encryption algorithm to use (default: AES-256-GCM)
 * @param recoveryHint - Optional hint for passphrase recovery
 * @param onProgress - Progress callback (0-100)
 * @returns Encryption result with all necessary data for decryption
 *
 * @example
 * const result = await encryptSeedPhraseWithProgress(
 *   "abandon abandon abandon ... art",
 *   "my-super-strong-passphrase-2024",
 *   "AES-256-GCM",
 *   "Prénom de ma mère + année de naissance",
 *   (progress) => console.log(`Progress: ${progress}%`)
 * );
 */
export async function encryptSeedPhraseWithProgress(
  seedPhrase: string,
  passphrase: string,
  algorithm: "AES-256-GCM" | "XChaCha20-Poly1305" = "AES-256-GCM",
  recoveryHint?: string,
  onProgress?: (progress: number) => void
): Promise<EncryptionResult> {
  if (!seedPhrase || seedPhrase.trim().length === 0) {
    throw new Error('Seed phrase cannot be empty');
  }

  if (!passphrase || passphrase.length < 8) {
    throw new Error('Passphrase must be at least 8 characters');
  }

  try {
    // Initial progress
    onProgress?.(0);

    // Generate random salt (32 bytes)
    const salt = randomBytes(32);
    onProgress?.(10);

    // Derive encryption key using Argon2id (this is the heavy part)
    // Progress will be updated from 10% to 70% during key derivation
    const key = await deriveKeyAsync(passphrase, salt, undefined, (kdfProgress) => {
      // Map KDF progress (0-100) to overall progress (10-70)
      const mappedProgress = 10 + (kdfProgress / 100) * 60;
      onProgress?.(mappedProgress);
    });

    onProgress?.(70);

    // Convert seed phrase to bytes
    const plaintext = utf8ToBytes(seedPhrase.trim());

    let iv: Uint8Array;
    let ciphertext: Uint8Array;
    let tag: Uint8Array;
    let encrypted: Uint8Array;

    if (algorithm === "XChaCha20-Poly1305") {
      // Generate random IV (24 bytes for XChaCha20)
      iv = randomBytes(24);

      // Encrypt with XChaCha20-Poly1305
      const cipher = xchacha20poly1305(key, iv);
      ciphertext = cipher.encrypt(plaintext);

      // Extract authentication tag (last 16 bytes)
      tag = ciphertext.slice(-16);
      encrypted = ciphertext.slice(0, -16);
    } else {
      // AES-256-GCM (default)
      // Generate random IV (12 bytes for GCM)
      iv = randomBytes(12);

      // Encrypt with AES-256-GCM
      const cipher = gcm(key, iv);
      ciphertext = cipher.encrypt(plaintext);

      // Extract authentication tag (last 16 bytes)
      tag = ciphertext.slice(-16);
      encrypted = ciphertext.slice(0, -16);
    }

    onProgress?.(90);

    // Calculate checksum for integrity verification
    const checksumHash = sha256(encrypted);
    const checksum = toBase64(checksumHash);

    onProgress?.(100);

    return {
      ciphertext: toBase64(encrypted),
      salt: toBase64(salt),
      iv: toBase64(iv),
      tag: toBase64(tag),
      version: 'v1',
      timestamp: Date.now(),
      algorithm,
      kdf: 'Argon2id',
      kdfParams: {
        memory: ARGON2_PARAMS.memory,
        iterations: ARGON2_PARAMS.iterations,
        parallelism: ARGON2_PARAMS.parallelism,
      },
      checksum,
      recoveryHint,
    };
  } catch (error) {
    throw new Error(`Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Decrypt seed phrase with passphrase
 *
 * @param encryptedData - The encryption result from encryptSeedPhrase
 * @param passphrase - User's decryption passphrase (must match encryption)
 * @returns Decrypted seed phrase
 *
 * @throws Error if passphrase is incorrect or data is corrupted
 *
 * @example
 * const seedPhrase = await decryptSeedPhrase(encryptedData, "my-super-strong-passphrase-2024");
 */
export async function decryptSeedPhrase(
  encryptedData: EncryptionResult,
  passphrase: string
): Promise<string> {
  if (!passphrase || passphrase.length === 0) {
    throw new Error('Passphrase cannot be empty');
  }

  try {
    // Parse Base64 data
    const salt = fromBase64(encryptedData.salt);
    const iv = fromBase64(encryptedData.iv);
    const encrypted = fromBase64(encryptedData.ciphertext);
    const tag = fromBase64(encryptedData.tag);

    // Verify checksum if present (for data integrity)
    if (encryptedData.checksum) {
      const calculatedChecksum = toBase64(sha256(encrypted));
      if (calculatedChecksum !== encryptedData.checksum) {
        throw new Error('Data corruption detected: checksum mismatch');
      }
    }

    // Derive key using same parameters as encryption
    const key = deriveKey(passphrase, salt, encryptedData.kdfParams);

    // Combine ciphertext and tag
    const ciphertextWithTag = new Uint8Array(encrypted.length + tag.length);
    ciphertextWithTag.set(encrypted);
    ciphertextWithTag.set(tag, encrypted.length);

    let plaintext: Uint8Array;

    // Detect algorithm and decrypt accordingly
    if (encryptedData.algorithm === "XChaCha20-Poly1305") {
      // Decrypt with XChaCha20-Poly1305
      const cipher = xchacha20poly1305(key, iv);
      plaintext = cipher.decrypt(ciphertextWithTag);
    } else {
      // Decrypt with AES-256-GCM (default/legacy)
      const cipher = gcm(key, iv);
      plaintext = cipher.decrypt(ciphertextWithTag);
    }

    // Convert bytes back to string
    return bytesToUtf8(plaintext);
  } catch (error) {
    // Authentication failure or wrong passphrase
    throw new Error('Decryption failed: Invalid passphrase or corrupted data');
  }
}

/**
 * Calculate passphrase strength
 *
 * @param passphrase - The passphrase to evaluate
 * @returns Score from 0-4 (0=very weak, 4=very strong)
 */
export function getPassphraseStrength(passphrase: string): {
  score: number;
  label: string;
  color: string;
  suggestions: string[];
} {
  const length = passphrase.length;
  let score = 0;
  const suggestions: string[] = [];

  // Length scoring
  if (length >= 20) score = 4;
  else if (length >= 16) score = 3;
  else if (length >= 12) score = 2;
  else if (length >= 8) score = 1;
  else score = 0;

  // Character variety bonus
  const hasLower = /[a-z]/.test(passphrase);
  const hasUpper = /[A-Z]/.test(passphrase);
  const hasNumber = /[0-9]/.test(passphrase);
  const hasSpecial = /[^a-zA-Z0-9]/.test(passphrase);

  const variety = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

  if (variety >= 3) score = Math.min(4, score + 1);
  else if (variety === 2) score = Math.min(4, score);
  else score = Math.max(0, score - 1);

  // Suggestions
  if (length < 12) suggestions.push('Use at least 12 characters');
  if (!hasUpper) suggestions.push('Add uppercase letters');
  if (!hasNumber) suggestions.push('Add numbers');
  if (!hasSpecial) suggestions.push('Add special characters (!@#$%^&*)');
  if (length < 16) suggestions.push('Longer passphrases are more secure');

  // Labels and colors
  const labels = ['Very Weak', 'Weak', 'Medium', 'Strong', 'Very Strong'];
  const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];

  return {
    score,
    label: labels[score],
    color: colors[score],
    suggestions,
  };
}

/**
 * Validate seed phrase format (basic)
 *
 * @param seedPhrase - The seed phrase to validate
 * @returns true if format appears valid
 */
export function validateSeedPhrase(seedPhrase: string): {
  isValid: boolean;
  wordCount: number;
  errors: string[];
} {
  const errors: string[] = [];
  const trimmed = seedPhrase.trim();

  if (!trimmed) {
    errors.push('Seed phrase cannot be empty');
    return { isValid: false, wordCount: 0, errors };
  }

  const words = trimmed.split(/\s+/);
  const wordCount = words.length;

  // Check word count (1 to 40 words)
  if (wordCount < 1) {
    errors.push('Seed phrase must contain at least 1 word');
  } else if (wordCount > 40) {
    errors.push(`Maximum 40 words allowed, found ${wordCount}`);
  }

  // Check for empty words
  if (words.some(word => !word || word.length === 0)) {
    errors.push('Seed phrase contains empty words');
  }

  // Check for non-alphabetic characters (basic check)
  if (words.some(word => !/^[a-z]+$/i.test(word))) {
    errors.push('Seed phrase should only contain alphabetic words');
  }

  return {
    isValid: errors.length === 0,
    wordCount,
    errors,
  };
}

/**
 * Export encrypted seed as JSON file
 */
export function exportEncryptedSeed(
  encryptedData: EncryptionResult,
  label: string = 'My Seed Vault'
): Blob {
  const exportData = {
    version: '2.0',
    type: 'encrypted-seed-phrase',
    label,
    exportDate: new Date().toISOString(),
    encrypted: encryptedData,
  };

  const json = JSON.stringify(exportData, null, 2);
  return new Blob([json], { type: 'application/json' });
}

/**
 * Parse imported JSON file
 */
export async function importEncryptedSeed(file: File): Promise<EncryptionResult> {
  const text = await file.text();

  try {
    const data = JSON.parse(text);

    if (data.type === 'encrypted-seed-phrase' && data.encrypted) {
      return data.encrypted as EncryptionResult;
    }

    // Try to parse as raw EncryptionResult
    if (data.ciphertext && data.salt && data.iv && data.tag) {
      return data as EncryptionResult;
    }

    throw new Error('Invalid file format');
  } catch (error) {
    throw new Error('Failed to parse encrypted seed file');
  }
}
